import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { Building, Upload, X, Crop } from 'lucide-react';
import Cropper from 'react-easy-crop';
import travelAgentService from '../services/travelAgentService';
import MainLayout from '../components/Layout/MainLayout';
import { ACCOUNT_PAGES_HERO_IMAGE_URL } from '../constants/accountPagesHeroImage';
import { getCroppedSquarePng } from '../utils/cropImage';

const WhiteLabelingPage = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [formData, setFormData] = useState({
    legalBusinessName: '',
    businessAddress: '',
    businessPhoneNumber: '',
    businessEmail: '',
    websiteUrl: ''
  });

  // Image Crop State
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const response = await travelAgentService.getProfile();
      if (response.data) {
        setProfile(response.data);
        setFormData({
          legalBusinessName: response.data.legalBusinessName || '',
          businessAddress: response.data.businessAddress || '',
          businessPhoneNumber: response.data.businessPhoneNumber || '',
          businessEmail: response.data.businessEmail || '',
          websiteUrl: response.data.websiteUrl || ''
        });
        if (response.data.companyLogoUrl) {
          setLogoPreview(response.data.companyLogoUrl);
        }
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Failed to load profile');
    }
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Please select a valid image file (JPEG, JPG, or PNG)');
        return;
      }

      // Validate file size (2MB limit)
      if (file.size > 2 * 1024 * 1024) {
        toast.error('Logo file size must be less than 2MB');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setImageToCrop(e.target.result);
        setShowCropModal(true);
      };
      reader.readAsDataURL(file);
      // Reset input value to allow selecting same file again
      e.target.value = '';
    }
  };

  const onCropComplete = useCallback((_croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCropSave = async () => {
    try {
      const { blob, objectUrl } = await getCroppedSquarePng(imageToCrop, croppedAreaPixels);
      
      setLogoFile(blob);
      setLogoPreview(objectUrl);
      setShowCropModal(false);
      setImageToCrop(null);
      toast.success('Image cropped successfully');
    } catch (error) {
      console.error('Failed to crop image:', error);
      toast.error('Failed to crop image');
    }
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setImageToCrop(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('legalBusinessName', formData.legalBusinessName);
      formDataToSend.append('businessAddress', formData.businessAddress);
      formDataToSend.append('businessPhoneNumber', formData.businessPhoneNumber);
      formDataToSend.append('businessEmail', formData.businessEmail);
      formDataToSend.append('websiteUrl', formData.websiteUrl);

      if (logoFile) {
        formDataToSend.append('logo', logoFile, 'company-logo.png');
      }

      const response = await travelAgentService.updateCompanyDetails(formDataToSend);

      if (response.success) {
        toast.success('Company details updated successfully');
        loadProfile(); // Reload profile to get updated data
        setLogoFile(null);
      } else {
        toast.error(response.msg || 'Failed to update company details');
      }
    } catch (error) {
      console.error('Error updating company details:', error);
      toast.error('Failed to update company details');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="min-h-screen bg-slate-50/50">
        {/* Hero Section */}
        <section className="relative h-96 bg-black overflow-hidden">
          <div className='h-12'></div>
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-55"
            style={{ backgroundImage: `url("${ACCOUNT_PAGES_HERO_IMAGE_URL}")` }}
            aria-hidden
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 via-black/10 to-black/30"></div>
          <div className="relative z-10 h-full flex items-center">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                <p className="text-sm text-slate-300 font-medium tracking-wider uppercase">Company Settings</p>
              </div>
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-3">
                White Labeling
              </h2>
              <p className="text-slate-300 text-lg max-w-2xl mb-6">
                Customize your company branding for white-labeled documents
              </p>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pb-12 relative z-20">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-200 p-8">

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Logo Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-900">Company Logo</h2>
              <div className="flex items-center space-x-6">
                <div className="flex-shrink-0">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt="Company Logo"
                      className="w-24 h-24 object-contain border border-slate-200 rounded-lg"
                      onError={() => setLogoPreview(null)}
                    />
                  ) : (
                    <div className="w-24 h-24 border-2 border-dashed border-slate-300 rounded-lg flex items-center justify-center">
                      <span className="text-slate-400 text-sm">No Logo</span>
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <label className="block">
                    <span className="text-sm font-medium text-slate-700">Upload Logo</span>
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png"
                      onChange={handleLogoChange}
                      className="mt-1 block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                    />
                  </label>
                  <p className="mt-1 text-xs text-slate-500">
                    JPEG, JPG, or PNG. Max size: 2MB. Square image required (cropping tool provided).
                  </p>
                </div>
              </div>
            </div>

            {/* Company Details */}
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-slate-900">Company Details</h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Legal Business Name
                  </label>
                  <input
                    type="text"
                    name="legalBusinessName"
                    value={formData.legalBusinessName}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Business Email
                  </label>
                  <input
                    type="email"
                    name="businessEmail"
                    value={formData.businessEmail}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Business Phone Number
                  </label>
                  <input
                    type="tel"
                    name="businessPhoneNumber"
                    value={formData.businessPhoneNumber}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Website URL
                  </label>
                  <input
                    type="url"
                    name="websiteUrl"
                    value={formData.websiteUrl}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                    placeholder="https://"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Business Address
                </label>
                <textarea
                  name="businessAddress"
                  value={formData.businessAddress}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-slate-500 transition-colors"
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-6 border-t border-slate-200">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-slate-800 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors duration-200"
              >
                {loading ? 'Updating...' : 'Update Company Details'}
              </button>
            </div>
          </form>
          </div>

          {/* Crop Modal */}
          {showCropModal && (
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Crop className="w-5 h-5 text-slate-600" />
                    <h3 className="text-lg font-semibold text-slate-900">Crop Logo</h3>
                  </div>
                  <button 
                    type="button"
                    onClick={handleCropCancel}
                    className="p-1 hover:bg-slate-100 rounded-full transition-colors"
                  >
                    <X className="w-5 h-5 text-slate-500" />
                  </button>
                </div>
                
                <div className="relative flex-1 min-h-[350px] bg-slate-50">
                  <Cropper
                    image={imageToCrop}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>

                <div className="p-6 space-y-6">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm font-medium text-slate-600">
                      <span>Zoom</span>
                      <span>{Math.round(zoom * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      value={zoom}
                      min={1}
                      max={3}
                      step={0.1}
                      aria-labelledby="Zoom"
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-slate-800"
                    />
                  </div>

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={handleCropCancel}
                      className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-700 rounded-xl hover:bg-slate-50 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleCropSave}
                      className="flex-1 px-4 py-2.5 bg-slate-800 text-white rounded-xl hover:bg-slate-700 font-medium transition-colors"
                    >
                      Crop & Save
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default WhiteLabelingPage;